import { MetricIdentifier } from 'modules/experiments/constants';
import {
  BasePageExperimentData,
  BasePageExperimentVariantProperties,
  ExperimentAnalyticsTranslation,
  ExperimentBadgeMetadata,
  ExperimentDetailsCardMetadata,
  ExperimentMetricsOptionsAndDefault,
  ExperimentMonitorHeaderMetaData,
  ExperimentSummaryArray,
  ExperimentTimeFilterMetaData,
  ExperimentVariantBreakdownArray,
  OverviewTabExperimentData,
  PerformanceTabExperimentData,
} from 'modules/experiments/types';
import {
  calculateLift,
  determineExperimentState,
  getControlVariant,
  getConversionEventMetrics,
  getMetricOptions,
  getMinutesLeftInExperiment,
  getMinutesRunInExperiment,
  getNonControlVariationCount,
  getStatusMappingIfWinnerVariationsExist,
  getVariantDetails,
  getVariantMetrics,
  getWinnerVariant,
  getWinningCriteriaMetric,
  isExperimentInMABMode,
  isTriggerCampaignType,
  sortControlVariantFirst,
} from 'modules/experiments/utils';
import { AnalyticsMetric, ExperimentWinningCriteriaDefinition } from 'modules/graphql';
import { createSelector } from 'reselect';

const getAnalyticsPage = (state: Record<string, any>) => state.experiments.analyticsPage;

export const getExperimentData = (state: Record<string, any>) =>
  getAnalyticsPage(state).basePage.data;
export const getExperimentCampaignData = (state: Record<string, any>) =>
  getAnalyticsPage(state).basePage.data.campaign;
export const getExperimentLoading = (state: Record<string, any>) =>
  getAnalyticsPage(state).basePage.pageLoading;
export const getExperimentHasError = (state: Record<string, any>) =>
  getAnalyticsPage(state).basePage.pageError;
export const getExperimentAnalyticMetrics = (state: Record<string, any>) =>
  getAnalyticsPage(state).metrics;
export const getExperimentMetricTranslationData = (state: Record<string, any>) =>
  getAnalyticsPage(state).i18n.metricTranslations;
export const getExperimentTranslationData = (state: Record<string, any>) =>
  getAnalyticsPage(state).i18n.translations;

export const getExperimentSends = (state: Record<string, any>) =>
  getAnalyticsPage(state).overviewTab.data;
export const getExperimentOverviewTabLoadingState = (state: Record<string, any>) =>
  getAnalyticsPage(state).overviewTab.overviewTabLoading;
export const getExperimentOverviewTabErrorState = (state: Record<string, any>) =>
  getAnalyticsPage(state).overviewTab.overviewTabError;

export const getExperimentMetrics = (state: Record<string, any>) =>
  getAnalyticsPage(state).performanceTab.data;
export const getExperimentPerformanceTabLoadingState = (state: Record<string, any>) =>
  getAnalyticsPage(state).performanceTab.performanceTabLoading;
export const getExperimentPerformanceTabErrorState = (state: Record<string, any>) =>
  getAnalyticsPage(state).performanceTab.performanceTabError;

export const getExperimentLoadingState = createSelector(
  [getExperimentLoading, getExperimentData],
  // There is a little weirdness on when the loading state changes and when the experiment
  // data is actually available. So, checking the experiment id to make sure that the
  // actual experiment data has been set properly.
  (loadingState: boolean, experiment: BasePageExperimentData) =>
    loadingState || experiment.id === 0,
);

export const getExperimentStateStatus = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData) => experiment.state,
);

export const getExperimentStartDate = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData) => experiment.startedAt,
);

export const getExperimentVariants = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData) => experiment.variations,
);

export const getExperimentId = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData) => experiment.id,
);

export const getExperimentName = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData) => experiment.name,
);

export const getExperimentType = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData) => experiment.type,
);

export const getCampaignType = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData) => experiment.campaign.type,
);

export const getCampaignMessageMedium = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData) => experiment.campaign.messageMedium,
);

export const getExperimentHasWinner = createSelector(
  [getExperimentVariants],
  (variants: BasePageExperimentVariantProperties[]) => {
    // Should either have 1 or 0 winners in experiment
    const winnerArray = variants.filter(({ winner }) => {
      return winner === true;
    });
    return !!winnerArray.length;
  },
);

export const getExperimentTranslations = createSelector(
  [getExperimentTranslationData],
  (translations: ExperimentAnalyticsTranslation) => translations,
);

export const isExperimentInMultiArmedBanditMode = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData) => {
    const { endedAt, exploitationStartedAt } = experiment;
    const isTriggerCampaign = isTriggerCampaignType(experiment.campaign.type);

    return isExperimentInMABMode(isTriggerCampaign, endedAt, exploitationStartedAt);
  },
);

export const getExperimentHeaderMetadata = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData): ExperimentMonitorHeaderMetaData => ({
    name: experiment.name,
    id: experiment.id,
    messageMedium: experiment.campaign.messageMedium,
    createdAt: experiment.createdAt,
    campaignName: experiment.campaign.name,
    campaignType: experiment.campaign.type,
    campaignId: experiment.campaign.id,
    experimentState: determineExperimentState(experiment),
    campaignState: experiment.campaign.state,
  }),
);

export const getExperimentTimeFilterMetadata = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData): ExperimentTimeFilterMetaData => ({
    variationCount: getNonControlVariationCount(experiment.variations),
    experimentStartDate: experiment.startedAt,
    experimentEndDate: experiment.endedAt,
    experimentState: experiment.state,
  }),
);

export const getExperimentWinningCriteria = createSelector(
  [getExperimentData],
  (experiment: BasePageExperimentData): ExperimentWinningCriteriaDefinition => ({
    name: experiment.winningCriteria.name,
    type: experiment.winningCriteria.type,
  }),
);

export const getExperimentVariantSummary = createSelector(
  [getExperimentData, getExperimentSends],
  (
    experiment: BasePageExperimentData,
    experimentSends: OverviewTabExperimentData,
  ): ExperimentSummaryArray => {
    const controlVariant = experiment.variations.find((variant) => variant.control);
    const controlVariantSends =
      experimentSends.variations.find(({ id }) => id === controlVariant?.id)?.experimentSends || 0;
    const controlVariantWinningCriteria =
      experimentSends.variations.find(({ id }) => id === controlVariant?.id)
        ?.experimentWinningCriteria || 0;

    const controlVariantMetrics = {
      controlExperimentWinningCriteria: controlVariantWinningCriteria,
      controlExperimentSends: controlVariantSends,
    };
    const experimentVariantSummary = sortControlVariantFirst(
      experiment.variations.map((variant) => {
        const variantSends =
          (experimentSends.variations.find(({ id }) => variant.id === id) ?? {}).experimentSends ||
          0;
        const variantWinningCriteria =
          (experimentSends.variations.find(({ id }) => variant.id === id) ?? {})
            .experimentWinningCriteria || 0;

        return {
          ...getVariantDetails(experiment, variant),
          ...controlVariantMetrics,
          experimentID: experiment.id,
          sends: variantSends,
          experimentWinningCriteria: variantWinningCriteria,
        };
      }),
    );

    return experiment.holdout
      ? [
          ...experimentVariantSummary,
          {
            userCount: experimentSends.holdout?.holdoutUserCount,
            winningCriteriaCount: experimentSends.holdout?.experimentWinningCriteria,
            holdoutGroupPercent: experiment.holdout?.percent,
          },
        ]
      : experimentVariantSummary;
  },
);

export const getExperimentVariantBreakdown = createSelector(
  [
    getExperimentData,
    getExperimentMetrics,
    getExperimentAnalyticMetrics,
    getExperimentTranslationData,
  ],
  (
    experiment: BasePageExperimentData,
    experimentMetrics: PerformanceTabExperimentData,
    experimentMetricsList: AnalyticsMetric[],
    translations: ExperimentAnalyticsTranslation,
  ): ExperimentVariantBreakdownArray => {
    const metricDisplayTypeMap = experimentMetricsList.reduce(
      (acc, { metricName, valueType }) => ({ ...acc, [metricName]: valueType }),
      {},
    );
    const experimentVariants = sortControlVariantFirst(
      experiment.variations.map((variant) => {
        const variantMetrics =
          (experimentMetrics.variations.find(({ id }) => variant.id === id) ?? {}).metrics || [];

        return {
          ...getVariantDetails(experiment, variant),
          [MetricIdentifier.SYSTEM_METRICS]: getVariantMetrics(
            variantMetrics,
            metricDisplayTypeMap,
          ),
          [MetricIdentifier.CONVERSION]: getConversionEventMetrics(
            variantMetrics,
            translations,
            false,
          ),
          [MetricIdentifier.UNIQUE_CONVERSION]: getConversionEventMetrics(
            variantMetrics,
            translations,
            true,
          ),
        };
      }),
    );
    return experimentMetrics.holdout
      ? [
          ...experimentVariants,
          {
            userCount: experimentMetrics.holdout?.holdoutUserCount,
            winningCriteriaCount: experimentMetrics.holdout?.experimentWinningCriteria,
            holdoutGroupPercent: experiment.holdout?.percent,
            [MetricIdentifier.SYSTEM_METRICS]: getVariantMetrics(
              experimentMetrics.holdout.metrics,
              metricDisplayTypeMap,
            ),
            [MetricIdentifier.CONVERSION]: getConversionEventMetrics(
              experimentMetrics.holdout.metrics,
              translations,
              false,
            ),
            [MetricIdentifier.UNIQUE_CONVERSION]: getConversionEventMetrics(
              experimentMetrics.holdout.metrics,
              translations,
              true,
            ),
          },
        ]
      : experimentVariants;
  },
);

export const getExperimentMetricsOptionAndDefault = createSelector(
  [
    getExperimentData,
    getExperimentAnalyticMetrics,
    getExperimentMetricTranslationData,
    getExperimentTranslationData,
  ],
  (
    experiment: BasePageExperimentData,
    experimentMetricsList: AnalyticsMetric[],
    metricTranslations: Record<string, string>,
    translations: ExperimentAnalyticsTranslation,
  ): ExperimentMetricsOptionsAndDefault => ({
    options: getMetricOptions({
      messageMedium: experiment.campaign.messageMedium,
      winningCriteriaName: experiment.winningCriteria.name,
      experimentMetricsList,
      metricTranslations,
      translations,
      campaignConversions:
        experiment.campaign.conversions?.map(({ events }) => events[0]?.name) ?? [],
    }),
    defaultValue: [
      getWinningCriteriaMetric(
        experiment.campaign.messageMedium,
        experiment.winningCriteria.name,
        translations,
      ),
    ],
  }),
);

export const getExperimentDetailsCardMetadata = createSelector(
  [getExperimentData],
  (analyticsData: BasePageExperimentData): ExperimentDetailsCardMetadata => ({
    experimentType: analyticsData.type,
    experimentTestPeriod: analyticsData.testDurationInMinutes,
    experimentStart: analyticsData.startedAt,
    experimentEnd: analyticsData.endedAt,
    experimentID: analyticsData.id,
    createdBy: analyticsData.createdBy.id,
    experimentState: determineExperimentState(analyticsData),
    winningCriteria: analyticsData.winningCriteria.name,
    holdout: analyticsData.holdout,
    randomlySplitVariants: analyticsData.evenlySplitVariations,
    initialSendSize: analyticsData.testSizePercentage,
    campaignType: analyticsData.campaign.type,
    testSendsPerVariation: analyticsData.testSendsPerVariation,
    attributionPeriodHours: analyticsData.attributionPeriodHours,
  }),
);

export const getExperimentSummaryBadgeMetadata = createSelector(
  [getExperimentData, getExperimentSends],
  (
    experiment: BasePageExperimentData,
    experimentSends: OverviewTabExperimentData,
  ): ExperimentBadgeMetadata => {
    const totalUsersSend = experimentSends.variations.reduce(
      (accumulator, currentValue) => accumulator + currentValue.experimentSends,
      0,
    );

    const controlVariant = getControlVariant(experiment.variations);
    const controlVariantSends =
      experimentSends.variations.find(({ id }) => id === controlVariant?.id)?.experimentSends || 0;
    const controlVariantWinningCriteria =
      experimentSends.variations.find(({ id }) => id === controlVariant?.id)
        ?.experimentWinningCriteria || 0;

    const winnerVariant = getWinnerVariant(experiment.variations);
    const winnerVariantMapping = getStatusMappingIfWinnerVariationsExist(winnerVariant);
    const winningVariantSends =
      experimentSends.variations.find(({ id }) => id === winnerVariant?.id)?.experimentSends || 0;
    const winningVariantWinningCriteria =
      experimentSends.variations.find(({ id }) => id === winnerVariant?.id)
        ?.experimentWinningCriteria || 0;

    const winningLift = calculateLift({
      variantSends: winningVariantSends,
      variantExperimentWinningCriteria: winningVariantWinningCriteria,
      controlExperimentSends: controlVariantSends,
      controlExperimentWinningCriteria: controlVariantWinningCriteria,
    });

    const minutesLeftInExperiment = getMinutesLeftInExperiment({
      startedAt: experiment.startedAt,
      testDurationInMinutes: experiment.testDurationInMinutes,
    });

    const experimentTestPeriod = getMinutesRunInExperiment(experiment.startedAt);

    return {
      variantWinnerType: winnerVariantMapping,
      experimentState: experiment.state,
      randomlySplitVariations: experiment.evenlySplitVariations,
      campaignType: experiment.campaign.type,
      winnerName: winnerVariant?.name || '',
      winnerLift: winningLift.toFixed(2) || '',
      experimentTestPeriod,
      totalUserSends: totalUsersSend,
      minutesLeftInExperiment,
      testSendsPerVariation: experiment?.testSendsPerVariation,
    };
  },
);
